import { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Paper, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Chip, Grid, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { Add } from '@mui/icons-material';
import { useGuiaSalidaStore } from '../store/guiaSalidaStore';
import { guiaSalidaService } from '../services/guiaSalidaService';
import { initialGuiaSalidaState } from '../types/guiaSalida.types';
import { almacenService } from '../../almacenes/services/almacenService';
import type { Deposito } from '../../almacenes/types/almacen.types';

const GuiasSalidaPage = () => {
  const { guias, loading, setGuias, setLoading } = useGuiaSalidaStore();
  const [depositos, setDepositos] = useState<Deposito[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState(initialGuiaSalidaState);

  useEffect(() => { loadData(); loadDepositos(); }, []);

  const loadDepositos = async () => {
    try { setDepositos(await almacenService.getAll()); } catch { /* handled */ }
  };

  const loadData = async () => {
    setLoading(true);
    try { setGuias(await guiaSalidaService.getAll()); } finally { setLoading(false); }
  };

  const handleSave = async () => {
    try {
      await guiaSalidaService.save(formData);
      setOpenDialog(false);
      setFormData(initialGuiaSalidaState);
      await loadData();
    } catch { /* handled */ }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Eliminar esta guía de salida?')) return;
    try { await guiaSalidaService.delete(id); await loadData(); } catch { /* handled */ }
  };

  const columns: GridColDef[] = [
    { field: 'idMovimiento', headerName: 'ID', width: 70 },
    { field: 'numeroGuia', headerName: 'N° Guía', width: 150 },
    { field: 'depositoOrigenNombre', headerName: 'Almacén Origen', width: 150 },
    { field: 'direccionDestino', headerName: 'Destino', flex: 1, minWidth: 180 },
    { field: 'motivoTraslado', headerName: 'Motivo', width: 150 },
    { field: 'transportistaDescripcion', headerName: 'Transportista', width: 150 },
    { field: 'placaVehiculo', headerName: 'Placa', width: 100 },
    { field: 'fechaTraslado', headerName: 'Fecha Traslado', width: 130,
      valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : '-' },
    { field: 'estado', headerName: 'Estado', width: 100,
      renderCell: (params) => <Chip label={params.value === 'V' ? 'Vigente' : 'Anulado'} size="small" color={params.value === 'V' ? 'success' : 'error'} />
    },
    { field: 'actions', headerName: 'Acciones', width: 100, sortable: false,
      renderCell: (params) => (
        <Button size="small" color="error" onClick={() => handleDelete(params.row.idMovimiento)}>Eliminar</Button>
      )
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">Guías de Salida</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setFormData(initialGuiaSalidaState); setOpenDialog(true); }}>
          Nueva Guía
        </Button>
      </Box>

      <Paper sx={{ height: 500 }}>
        <DataGrid rows={guias} columns={columns} getRowId={(row) => row.idMovimiento}
          loading={loading} density="compact"
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Paper>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Nueva Guía de Salida</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="N° Guía" value={formData.numeroGuia ?? ''}
                onChange={(e) => setFormData({ ...formData, numeroGuia: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Almacén Origen</InputLabel>
                <Select value={formData.idDepositoOrigen ?? ''} label="Almacén Origen"
                  onChange={(e) => setFormData({ ...formData, idDepositoOrigen: Number(e.target.value) })}>
                  {depositos.map((d) => <MenuItem key={d.idDeposito} value={d.idDeposito}>{d.nombre}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Dirección Destino" value={formData.direccionDestino ?? ''}
                onChange={(e) => setFormData({ ...formData, direccionDestino: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Motivo Traslado" value={formData.motivoTraslado ?? ''}
                onChange={(e) => setFormData({ ...formData, motivoTraslado: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Transportista" value={formData.transportistaDescripcion ?? ''}
                onChange={(e) => setFormData({ ...formData, transportistaDescripcion: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Placa Vehículo" value={formData.placaVehiculo ?? ''}
                onChange={(e) => setFormData({ ...formData, placaVehiculo: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" label="Chofer Nombre" value={formData.choferNombre ?? ''}
                onChange={(e) => setFormData({ ...formData, choferNombre: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" label="Chofer Documento" value={formData.choferDocumento ?? ''}
                onChange={(e) => setFormData({ ...formData, choferDocumento: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" label="Chofer Licencia" value={formData.choferLicencia ?? ''}
                onChange={(e) => setFormData({ ...formData, choferLicencia: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Fecha Traslado" type="date"
                value={formData.fechaTraslado ?? ''} InputLabelProps={{ shrink: true }}
                onChange={(e) => setFormData({ ...formData, fechaTraslado: e.target.value || null })} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" label="Observaciones" multiline rows={2}
                value={formData.observaciones ?? ''}
                onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GuiasSalidaPage;
